/**
 * Cuentas puras de la ayuda con Gavi: dónde va el hueco nítido, el
 * bocadillo y la flecha. Todo en píxeles de la ventana.
 */

export type Caja = {
  izq: number;
  arriba: number;
  der: number;
  abajo: number;
};

/** Caja que envuelve todas las dadas (o `null` si no hay ninguna). */
export function unirCajas(cajas: readonly Caja[]): Caja | null {
  if (cajas.length === 0) {
    return null;
  }
  return {
    izq: Math.min(...cajas.map(c => c.izq)),
    arriba: Math.min(...cajas.map(c => c.arriba)),
    der: Math.max(...cajas.map(c => c.der)),
    abajo: Math.max(...cajas.map(c => c.abajo)),
  };
}

/** La caja con `margen` px de aire alrededor, sin salirse de la ventana. */
export function ampliarCaja(
  caja: Caja,
  margen: number,
  ancho: number,
  alto: number,
): Caja {
  return {
    izq: Math.max(0, caja.izq - margen),
    arriba: Math.max(0, caja.arriba - margen),
    der: Math.min(ancho, caja.der + margen),
    abajo: Math.min(alto, caja.abajo + margen),
  };
}

function redondear(n: number): number {
  return Math.round(n * 10) / 10;
}

/**
 * Trazado SVG (regla `evenodd`) de toda la ventana con un agujero de esquinas
 * redondeadas: el velo borroso se recorta con él y deja nítido lo que Gavi
 * enseña. Sin caja, la ventana entera.
 */
export function rutaConHueco(
  caja: Caja | null,
  radioEsquina: number,
  ancho: number,
  alto: number,
): string {
  const marco = `M0 0H${ancho}V${alto}H0Z`;
  if (!caja) {
    return marco;
  }
  const { izq: l, arriba: t, der: r, abajo: b } = caja;
  const rad = Math.max(0, Math.min(radioEsquina, (r - l) / 2, (b - t) / 2));
  const n = redondear;
  return (
    `${marco}M${n(l + rad)} ${n(t)}H${n(r - rad)}` +
    `A${n(rad)} ${n(rad)} 0 0 1 ${n(r)} ${n(t + rad)}V${n(b - rad)}` +
    `A${n(rad)} ${n(rad)} 0 0 1 ${n(r - rad)} ${n(b)}H${n(l + rad)}` +
    `A${n(rad)} ${n(rad)} 0 0 1 ${n(l)} ${n(b - rad)}V${n(t + rad)}` +
    `A${n(rad)} ${n(rad)} 0 0 1 ${n(l + rad)} ${n(t)}Z`
  );
}

/** Hueco que se deja al bocadillo arriba (cabecera) y abajo (pestañas). */
export const MARGEN_BOCADILLO = { arriba: 70, abajo: 96, foco: 70 } as const;

/**
 * Dónde va el bocadillo: debajo del foco si el foco está en la mitad de
 * arriba, encima si está en la de abajo. Devuelve su `top` y si va debajo.
 */
export function colocarBocadillo(
  foco: Caja | null,
  altoBocadillo: number,
  alto: number,
): { arriba: number; debajo: boolean } {
  const maximo = Math.max(
    MARGEN_BOCADILLO.arriba,
    alto - altoBocadillo - MARGEN_BOCADILLO.abajo,
  );
  if (!foco) {
    return { arriba: Math.round((alto - altoBocadillo) / 2), debajo: true };
  }
  const debajo = (foco.arriba + foco.abajo) / 2 < alto * 0.45;
  const arriba = debajo
    ? Math.min(foco.abajo + MARGEN_BOCADILLO.foco, maximo)
    : Math.max(
        foco.arriba - altoBocadillo - MARGEN_BOCADILLO.foco,
        MARGEN_BOCADILLO.arriba,
      );
  return { arriba: Math.round(arriba), debajo };
}

/**
 * Flecha curva de latón del bocadillo al foco: el trazo (curva cuadrática que
 * se arquea hacia fuera) y el triángulo de la punta.
 */
export function rutaFlecha(
  desde: readonly [number, number],
  hasta: readonly [number, number],
): { trazo: string; punta: string } {
  const [x1, y1] = desde;
  const [x2, y2] = hasta;
  const mx = (x1 + x2) / 2 + (x2 > x1 ? -40 : 40);
  const my = (y1 + y2) / 2;
  const angulo = Math.atan2(y2 - my, x2 - mx);
  const lado = 9;
  const n = redondear;
  const p1 = [
    x2 - lado * Math.cos(angulo - 0.45),
    y2 - lado * Math.sin(angulo - 0.45),
  ];
  const p2 = [
    x2 - lado * Math.cos(angulo + 0.45),
    y2 - lado * Math.sin(angulo + 0.45),
  ];
  return {
    trazo: `M${n(x1)} ${n(y1)}Q${n(mx)} ${n(my)} ${n(x2)} ${n(y2)}`,
    punta: `M${n(x2)} ${n(y2)}L${n(p1[0])} ${n(p1[1])}L${n(p2[0])} ${n(p2[1])}Z`,
  };
}

/** ¿Se ve la caja entera entre la cabecera y la barra de pestañas? */
export function cajaVisible(caja: Caja, alto: number): boolean {
  return caja.arriba >= 0 && caja.abajo <= alto - MARGEN_BOCADILLO.abajo;
}

/** Caja de un elemento del DOM. */
export function cajaDe(el: Element): Caja {
  const r = el.getBoundingClientRect();
  return { izq: r.left, arriba: r.top, der: r.right, abajo: r.bottom };
}
